import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dateFormatter } from 'constants/index';
import { ADMIN_ROUTES } from 'constants/routes';
import { usePurchaseOrderListQuery } from 'api';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { OrderType, PaginationType, SortParamsType } from 'typings';
import { Form, FormInstance, TablePaginationConfig, TableProps } from 'components';
import { PurchaseOrderDataType } from './index';

const initialPagination: PaginationType = { page: 1, size: 10 };
const initialSort: SortParamsType = {
  field: null,
  order: null,
};
export const usePurchaseOrder = () => {
  const [openFilters, setOpenFilters] = useState(false);
  const [sort, setSort] = useState<SortParamsType>(initialSort);
  const [brandDrawerState, setBrandDrawerState] = useState<boolean>(false);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [pagination, setPagination] = useState<PaginationType>(initialPagination);
  const [brandsData, setBrandsData] = useState<Record<string, any>[] | undefined>();
  const [filtersForm] = Form.useForm<FormInstance>();
  const navigate = useNavigate();

  const { data: { content = [], totalElements = 0 } = {}, isLoading } = usePurchaseOrderListQuery({
    size: pagination.size,
    page: pagination.page,
    sortField: sort.field,
    sortOrder: sort.order,
    search,
    filters,
  });
  const onOpenFilters = useCallback(() => setOpenFilters(true), []);
  const onCloseFilters = useCallback(() => setOpenFilters(false), []);

  const onBrandDrawerExpand = useCallback((clients?: Record<string, any>[]) => {
    setBrandsData(clients);
    setBrandDrawerState((prevState) => !prevState);
  }, []);

  const routeChange = useCallback(
    (value: string) => {
      navigate(`${ADMIN_ROUTES.purchaseOrder.view}/${value}/general`);
    },
    [navigate]
  );
  const onAddNew = useCallback(() => {
    navigate(ADMIN_ROUTES.purchaseOrder.create);
  }, [navigate]);

  const handleTableChange: TableProps<PurchaseOrderDataType>['onChange'] = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<PurchaseOrderDataType> | SorterResult<PurchaseOrderDataType>[]
    ) => {
      const sortField = Array.isArray(sorter) ? sorter[0].columnKey : sorter.columnKey;
      const sortOrder = Array.isArray(sorter) ? sorter[0].order : sorter.order;
      const order = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined;
      setPagination({
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      });
      setSort({
        field: sortField as string,
        order: order as OrderType,
      });
    },
    []
  );

  const onSearch = useCallback((value: string) => {
    setPagination((oState) => ({
      ...oState,
      page: 1,
    }));
    setSearch(value);
  }, []);

  const applyFilters = useCallback(
    (values: Record<string, any>) => {
      const _filters: any = {
        ...(values.brandId && { brandId: values.brandId }),
        ...(values.supplierId && { supplierId: values.supplierId }),
        ...(values.assignedTo && { assignedTo: values.assignedTo }),
        ...(values.createdBy && { createdBy: values.createdBy }),
        ...(values.status && { status: values.status }),

        ...(values.deliveryDeadline?.[0] && {
          deliveryDeadlineFrom: dateFormatter(values.deliveryDeadline[0], 'start'),
        }),
        ...(values.deliveryDeadline?.[1] && {
          deliveryDeadlineTo: dateFormatter(values.deliveryDeadline[1], 'end'),
        }),
        ...(values.createdDate?.[0] && {
          createdDateFrom: dateFormatter(values.createdDate[0], 'start'),
        }),
        ...(values.createdDate?.[1] && {
          createdDateTo: dateFormatter(values.createdDate[1], 'end'),
        }),
        ...(values.lastModifiedDate?.[0] && {
          lastModifiedDateFrom: dateFormatter(values.lastModifiedDate[0], 'start'),
        }),
        ...(values.lastModifiedDate?.[1] && {
          lastModifiedDateTo: dateFormatter(values.lastModifiedDate[1], 'end'),
        }),
      };
      setFilters(_filters);
      setPagination((oState) => ({
        ...oState,
        page: 1,
      }));
      onCloseFilters();
    },
    [onCloseFilters]
  );
  const resetFilters = useCallback(() => setFilters({}), []);

  return {
    onOpenFilters,
    onCloseFilters,
    routeChange,
    data: content?.length ? content[0].purchaseRequestResponses : [],
    total: totalElements || 0,
    filtersForm,
    handleTableChange,
    openFilters,
    applyFilters,
    resetFilters,
    pagination,
    isLoading,
    onSearch,
    onAddNew,
    onBrandDrawerExpand,
    brandDrawerState,
    brandsData,
  };
};
